/*
 *  Campaign__Create_StartDateEditor.jsx
 *
 *
 *
 */
//==============================================================================
// External dep.
//==============================================================================
import React, { useEffect, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import DateTimePicker from "react-datetime-picker";
import { Button as BtnPrimary } from "@talentbait/shared-components";
//------------------------------------------------------------------------------
// Interal dep.
//------------------------------------------------------------------------------
import { CheckedIcon, ClearIcon } from "./Svg";
import { MIN_SELECTABLE_MINUTES_LEFT_TO_START, MIN_TIME_LEFT_TO_START_TEXT } from "./constants";
import { secsToHHMMSS, getSecondsLeft } from "./utils";
import { SidePanel, Tooltip } from "./Components";
import useComponentVisible from "../../../hooks/useComponentVisible";
import logger from "../../../utils/LoggerFrontend";
//------------------------------------------------------------------------------
// Styled Components
//------------------------------------------------------------------------------
const EditDateButton = styled.button`
  border-radius: 4px;
  transition: all 0.2s;
  background: #fff;
  color: #1e22ff;
  border: 2px solid #1e22ff;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  min-width: 200px;
  &:hover {
    color: #1e22ff;
    background-color: #1e22ff10;
  }
`;
const DateTimePickerWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  .react-datetime-picker__wrapper {
    border-radius: 4px;
    padding: 0 5px;
  }
`;
const DateTimePickerSidePanel = styled.div`
  padding: 10px;
`;
const DateTimeError = styled.div`
  width: 100%;
  font-family: Rubik;
  font-size: 13px;
  min-height: 13px;
  color: #ff0202;
  transition: all 0.2s;
  ${(props) =>
    props.active
      ? `
    margin-top: 0;
    margin-bottom: 6px;
    opacity: 1;
    visibility: visible;
  `
      : `
    margin-top: -13px;
    margin-bottom: 0;
    opacity: 0;
    visibility: hidden;
  `};
`;
const DateTimePickerActions = styled.div`
  display: flex;
  align-items: center;
`;
const CheckedButtonWrapper = styled.div`
  margin-right: 4px;
`;
const Checked = styled(CheckedIcon)`
  display: flex;
  width: 20px;
  height: 20px;
`;
const Clear = styled(ClearIcon)`
  display: flex;
  width: 20px;
  height: 20px;
`;
const TimeRemaining = styled.span`
  color: #1e22ff;
  font-weight: 600;
  min-width: 53px;
  display: inline-block;
  margin: 0px 4px;
`;
const StartDateEditorWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
const Description = styled.div`
  font-weight: 400;
  color: #302e38;
  font-size: 18px;
  line-height: 24px;
  margin-right: 10px;
`;
const CancelTimerButton = styled.span`
  color: #8e8fa3;
  font-weight: 600;
  line-height: 20px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    color: #4f5059;
  }
`;

const ActionsWrapper = styled.div`
  text-align: center;
`;
//------------------------------------------------------------------------------
// Main Functional Component
//------------------------------------------------------------------------------
const CampaignCreateStartDateEditor = ({
  textBeforeTime,
  textAfterTime,
  startingDate,
  changeStartingDate,
  setCampaignWaitingForApproval = () => {},
  secondsLeft,
  setSecondsLeft
}) => {
  const [dateTimePickerValue, setDateTimePickerValue] = useState(
    () => new Date(new Date(startingDate).getTime() + 5 * 60000) // add 5 minutes to starting date by default for the input
  );

  const {
    ref: dateTimePickerRef,
    isComponentVisible: isDateTimePickerVisible,
    setIsComponentVisible: setIsDateTimePickerVisible
  } = useComponentVisible(false);

  const openDateTimePicker = useCallback(
    () => setIsDateTimePickerVisible(true),
    [setIsDateTimePickerVisible]
  );
  const cancelDateTimePicker = useCallback(
    () => setIsDateTimePickerVisible(false),
    [setIsDateTimePickerVisible]
  );
  const saveNewDateTime = useCallback(() => {
    setIsDateTimePickerVisible(false);
    logger.log("saveNewDateTime", "called", {
      newStartingDate: dateTimePickerValue.toISOString(),
      changeStartingDate
    });
    changeStartingDate(dateTimePickerValue);
  }, [setIsDateTimePickerVisible, changeStartingDate, dateTimePickerValue]);

  const waitingForApprovalClicked = useCallback(() => {
    logger.log("waitingForApprovalClicked", "called", {
      setCampaignWaitingForApproval
    });
    setIsDateTimePickerVisible(false);
    setCampaignWaitingForApproval();
  }, [setCampaignWaitingForApproval, setIsDateTimePickerVisible]);

  const { validDateTime, errorDateTime } = useMemo(() => {
    const timeLeftInSeconds = getSecondsLeft(dateTimePickerValue, new Date());
    if (timeLeftInSeconds < MIN_SELECTABLE_MINUTES_LEFT_TO_START * 60) {
      return {
        validDateTime: false,
        errorDateTime: `Das Startdatum muss mindestens ${MIN_TIME_LEFT_TO_START_TEXT} in der Zukunft liegen`
      };
    }
    return { validDateTime: true };
  }, [dateTimePickerValue]);

  // Update seconds left when startingDate changes
  useEffect(() => {
    const currentDateObj = new Date();
    setSecondsLeft(getSecondsLeft(startingDate, currentDateObj));
  }, [setSecondsLeft, startingDate]);

  // Start (and stop) timer
  useEffect(() => {
    if (!secondsLeft) return;
    const intervalId = setInterval(() => {
      setSecondsLeft(secondsLeft - 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [secondsLeft, setSecondsLeft]);

  // Get time left in HH:MM:SS
  const timeLeft = useMemo(() => {
    if (secondsLeft < 259200) {
      return secsToHHMMSS(secondsLeft);
    } else if (!startingDate) {
      return "";
    } else {
      const startingDateString = startingDate.toLocaleString();
      return startingDateString.substring(0, startingDateString.length - 3);
    }
  }, [secondsLeft, startingDate]);
  if (timeLeft.includes("2021")) {
    textBeforeTime = textBeforeTime.replace(" in", " am");
  }

  return (
    <StartDateEditorWrapper>
      <Description>
        {textBeforeTime}
        <TimeRemaining>{timeLeft}</TimeRemaining>
        {textAfterTime}
      </Description>
      <ActionsWrapper>
        <EditDateButton onClick={openDateTimePicker}>Startdatum anpassen</EditDateButton>
        <CancelTimerButton
          data-tip=""
          data-for="cancel-timer"
          align="center"
          onClick={waitingForApprovalClicked}
        >
          Countdown stoppen
        </CancelTimerButton>
        <Tooltip
          id="cancel-timer"
          effect="solid"
          place="top"
          backgroundColor="#1C1928"
          offset={{ top: -5 }}
        >
          <span>
            Nachdem der Countdown deaktiviert wurde, können Sie die Kampagne nur noch manuell live
            schalten.
          </span>
        </Tooltip>
      </ActionsWrapper>
      <SidePanel
        active={isDateTimePickerVisible}
        side="right"
        height="auto"
        offset={6}
        width="240px"
      >
        <DateTimePickerSidePanel ref={dateTimePickerRef}>
          <DateTimeError className="preview-error" active={!!errorDateTime}>
            {errorDateTime}
          </DateTimeError>
          <DateTimePickerWrapper>
            <DateTimePicker
              onChange={setDateTimePickerValue}
              value={dateTimePickerValue}
              disableClock
              clearIcon={null}
              calendarIcon={null}
              locale="de-DE"
            />
            <DateTimePickerActions>
              <CheckedButtonWrapper>
                <BtnPrimary onClick={saveNewDateTime} size="small" disabled={!validDateTime}>
                  <Checked fill={validDateTime ? "#1E22FF" : "#a2a7b3"} />
                </BtnPrimary>
              </CheckedButtonWrapper>
              <BtnPrimary onClick={cancelDateTimePicker} size="small">
                <Clear />
              </BtnPrimary>
            </DateTimePickerActions>
          </DateTimePickerWrapper>
        </DateTimePickerSidePanel>
      </SidePanel>
    </StartDateEditorWrapper>
  );
};

export default CampaignCreateStartDateEditor;
